import { RefObject, useEffect, useRef, useState } from 'react';

import './useGrabScroll.css';

export default function useGrabToScroll(
  track: RefObject<HTMLDivElement | null> | null,
) {
  const isDown = useRef(false);
  const isDragging = useRef(false);
  const startX = useRef<number>(0);
  const startY = useRef<number>(0);
  const scrollLeft = useRef<number>(0);
  const velX = useRef<number>(0);
  const momentumId = useRef<number | null>(null);
  const dragThreshold = 5; // Minimum pixels to move before considering it a drag
  const [element, setElement] = useState<HTMLDivElement | null>(null);

  // Use a state to track when the element becomes available
  useEffect(() => {
    if (track?.current !== element) {
      setElement(track?.current || null);
    }
  });

  const handleMouseDown = (e: MouseEvent) => {
    if (!element) return;
    isDown.current = true;
    isDragging.current = false; // Reset dragging state
    element.classList.add('active-grab-scroll');
    startX.current = e.pageX - element.offsetLeft;
    startY.current = e.pageY - element.offsetTop;
    scrollLeft.current = element.scrollLeft;
    cancelMomentumTracking();
  };
  const handleMouseLeave = () => {
    if (!element) return;
    isDown.current = false;
    element.classList.remove('active-grab-scroll');
  };
  const handleMouseMove = (e: MouseEvent) => {
    if (!element) return;
    if (!isDown.current) return;

    const currentX = e.pageX - element.offsetLeft;
    const currentY = e.pageY - element.offsetTop;
    const deltaX = Math.abs(currentX - startX.current);
    const deltaY = Math.abs(currentY - startY.current);

    // Only start dragging if movement exceeds threshold
    if (
      !isDragging.current &&
      (deltaX > dragThreshold || deltaY > dragThreshold)
    ) {
      isDragging.current = true;
    }

    if (isDragging.current) {
      e.preventDefault();
      const walk = (currentX - startX.current) * 1; //scroll-fast
      const prevScrollLeft = element.scrollLeft;
      element.scrollLeft = scrollLeft.current - walk;
      velX.current = element.scrollLeft - prevScrollLeft;
    }
  };
  const handleClick = (e: MouseEvent) => {
    // Prevent link when dragging
    if (isDragging.current) {
      e.preventDefault();
      e.stopPropagation();
      isDragging.current = false;
      return false;
    }
  };
  const handleMouseUp = () => {
    if (!element) return;
    isDown.current = false;
    element.classList.remove('active-grab-scroll');
    beginMomentumTracking();
  };

  // Momentum
  const beginMomentumTracking = () => {
    cancelMomentumTracking();
    momentumId.current = requestAnimationFrame(momentumLoop);
  };
  const cancelMomentumTracking = () => {
    if (momentumId.current) {
      cancelAnimationFrame(momentumId.current);
    }
  };
  const momentumLoop = () => {
    if (!element) return;
    element.scrollLeft += velX.current;
    velX.current *= 0.95;
    if (Math.abs(velX.current) > 0.5) {
      momentumId.current = requestAnimationFrame(momentumLoop);
    }
  };

  useEffect(() => {
    // Don't set up event listeners on touch screen devices
    if (isTouchScreen() || !element) return;

    element.addEventListener('mousedown', handleMouseDown);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('mouseup', handleMouseUp);
    element.addEventListener('click', handleClick);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('wheel', cancelMomentumTracking);

    return () => {
      element.removeEventListener('mousedown', handleMouseDown);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('mouseup', handleMouseUp);
      element.removeEventListener('click', handleClick);
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('wheel', cancelMomentumTracking);
    };
  }, [element]); // Now depends on the tracked element state
}

const isTouchScreen = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    ('ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0)
  );
};
